import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Lock, Upload, FileText, CheckCircle2, Clock, Sparkles, Trash2, Image as ImageIcon, BookmarkPlus, Edit3, Plus } from "lucide-react";
import { useSavedPrompts } from "@/hooks/useSavedPrompts";

const TONE_MAX = 2000;

function relTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function fileSize(bytes) {
  if (!bytes) return "";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;
}

// Toggle switch (matches teal accent)
function Toggle({ checked, onChange, testid }) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${checked ? "bg-teal-600" : "bg-gray-300"}`}
      data-testid={testid}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? "translate-x-4" : "translate-x-0.5"}`}
      />
    </button>
  );
}

export default function PersonalizePage() {
  const { user } = useAuth();
  const isTrial = (user?.account_type || "").toLowerCase() === "trial";

  const [tone, setTone] = useState("");
  const [toneSavedAt, setToneSavedAt] = useState(null);
  const [savingTone, setSavingTone] = useState(false);
  const [toneEnabled, setToneEnabled] = useState(true);
  const [styleEnabled, setStyleEnabled] = useState(true);
  const [styleFiles, setStyleFiles] = useState([]);
  const [styleUploading, setStyleUploading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const styleInputRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/personalize`, { withCredentials: true });
      setTone(res.data.tone || "");
      setToneSavedAt(res.data.tone_updated_at);
      setToneEnabled(res.data.tone_enabled !== false);
      setStyleEnabled(res.data.style_enabled !== false);
      setStyleFiles(res.data.style_files || []);
      setRequests(res.data.template_requests || []);
    } catch {}
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveTone = async () => {
    setSavingTone(true);
    try {
      const res = await axios.post(`${API}/personalize/tone`, { tone }, { withCredentials: true });
      setTone(res.data.tone || "");
      setToneSavedAt(res.data.tone_updated_at);
      toast.success("Tone preference saved.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save tone");
    } finally {
      setSavingTone(false);
    }
  };

  const saveToggle = async (key, value) => {
    if (key === "tone_enabled") setToneEnabled(value);
    if (key === "style_enabled") setStyleEnabled(value);
    try {
      await axios.post(`${API}/personalize/toggles`, { [key]: value }, { withCredentials: true });
    } catch (err) {
      // revert on failure
      if (key === "tone_enabled") setToneEnabled(!value);
      if (key === "style_enabled") setStyleEnabled(!value);
      toast.error(err.response?.data?.detail || "Failed to update toggle");
    }
  };

  const onTemplateUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pptx")) {
      toast.error("Only .pptx files are accepted.");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/personalize/custom-template`, fd, { withCredentials: true });
      toast.success("Submitted for setup — we'll email you when your template is ready.");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit template");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onStyleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setStyleUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await axios.post(`${API}/personalize/style-upload`, fd, { withCredentials: true });
        setStyleFiles(prev => [...prev, res.data]);
      }
      toast.success(`${files.length} style reference${files.length === 1 ? "" : "s"} saved.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload style file");
    } finally {
      setStyleUploading(false);
      if (styleInputRef.current) styleInputRef.current.value = "";
    }
  };

  const removeStyleFile = async (id) => {
    try {
      await axios.delete(`${API}/personalize/style/${id}`, { withCredentials: true });
      setStyleFiles(prev => prev.filter(f => f.id !== id));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove file");
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="personalize-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-teal-600" />Personalize
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Make Wokelo speak your language and look like your brand.
        </p>
      </div>

      {/* ─── Custom Template ─── */}
      <Card className="border-gray-200 shadow-none relative overflow-hidden" data-testid="custom-template-card">
        <CardContent className="pt-5 pb-5">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Build custom template</p>
            {isTrial && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full" data-testid="custom-template-locked-pill">
                <Lock className="w-3 h-3" />Trial locked
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Upload your own PPTX and we'll set it up as a reusable template. Once ready, you'll see it in the template selector on the generator.
          </p>

          <div className={isTrial ? "opacity-50 pointer-events-none select-none" : ""}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pptx"
              onChange={onTemplateUpload}
              className="hidden"
              data-testid="custom-template-input"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || isTrial}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-black text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-50 transition-colors"
              data-testid="custom-template-upload-btn"
            >
              <Upload className="w-4 h-4" />
              {uploading ? "Submitting..." : "Upload PPTX template"}
            </button>
            <p className="text-[11px] text-gray-400 mt-2">.pptx only · up to 50 MB · review takes 1-2 business days</p>
          </div>

          {isTrial && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-100 rounded-lg flex items-start gap-2.5" data-testid="custom-template-trial-note">
              <Lock className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800">
                Custom templates are available on Starter, Pro, and Enterprise plans.
                <button onClick={() => window.dispatchEvent(new CustomEvent("credits:open-upgrade", { detail: { tab: "plans" } }))}
                   className="font-semibold underline ml-1 hover:text-amber-900"
                   data-testid="custom-template-upgrade-link">
                  Upgrade to unlock.
                </button>
              </p>
            </div>
          )}

          {requests.length > 0 && (
            <div className="mt-5 pt-4 border-t border-gray-100 space-y-2" data-testid="template-requests-list">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Your submissions</p>
              {requests.map(r => (
                <div key={r.id} className="flex items-center justify-between text-sm" data-testid={`template-request-${r.id}`}>
                  <div className="flex items-center gap-2 text-gray-700">
                    <FileText className="w-4 h-4 text-gray-400" />
                    <span className="font-medium truncate max-w-[280px]">{r.original_filename}</span>
                    <span className="text-xs text-gray-400">· {relTime(r.submitted_at)}</span>
                  </div>
                  {r.status === "ready" ? (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600">
                      <CheckCircle2 className="w-3.5 h-3.5" />Ready
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600">
                      <Clock className="w-3.5 h-3.5" />Pending review
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── Style Preferences ─── */}
      <Card className="border-gray-200 shadow-none" data-testid="style-prefs-card">
        <CardContent className="pt-5 pb-5">
          <div className="flex items-center justify-between gap-3 mb-1">
            <div className="flex-1">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Style preferences</p>
              <p className="text-sm text-gray-600 mt-1">
                Upload reference slides or images you'd like Wokelo to take inspiration from.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-[11px] font-semibold text-gray-500" data-testid="style-toggle-label">
                {styleEnabled ? "On" : "Off"}
              </span>
              <Toggle
                checked={styleEnabled}
                onChange={(v) => saveToggle("style_enabled", v)}
                testid="style-toggle"
              />
            </div>
          </div>

          <label className={`mt-3 flex flex-col items-center justify-center gap-2 px-4 py-6 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-colors ${styleUploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="style-prefs-dropzone">
            <Upload className="w-5 h-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">
              {styleUploading ? "Uploading..." : "Drag & drop or click to upload"}
            </span>
            <span className="text-[11px] text-gray-400">.pptx · .pdf · .jpg · .png · max 25 MB / file</span>
            <input
              ref={styleInputRef}
              type="file"
              multiple
              accept=".pptx,.pdf,.jpg,.jpeg,.png"
              className="hidden"
              onChange={onStyleUpload}
              data-testid="style-prefs-input"
            />
          </label>

          {styleFiles.length > 0 && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="style-files-grid">
              {styleFiles.map(f => (
                <div key={f.id} className="relative group border border-gray-200 rounded-lg overflow-hidden bg-gray-50" data-testid={`style-file-${f.id}`}>
                  {f.thumbnail ? (
                    <img src={f.thumbnail} alt={f.filename} className="w-full h-24 object-cover" />
                  ) : (
                    <div className="w-full h-24 flex items-center justify-center bg-gray-100">
                      <FileText className="w-8 h-8 text-gray-300" />
                    </div>
                  )}
                  <div className="px-2 py-1.5 bg-white border-t border-gray-100">
                    <p className="text-xs font-medium text-gray-700 truncate">{f.filename}</p>
                    <p className="text-[10px] text-gray-400">{fileSize(f.size_bytes)}</p>
                  </div>
                  <button
                    onClick={() => removeStyleFile(f.id)}
                    className="absolute top-1.5 right-1.5 p-1 bg-white/90 backdrop-blur rounded-full text-gray-500 hover:text-red-600 hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`remove-style-${f.id}`}
                    aria-label="Remove file"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── Tone Preferences ─── */}
      <Card className="border-gray-200 shadow-none" data-testid="tone-prefs-card">
        <CardContent className="pt-5 pb-5">
          <div className="flex items-center justify-between gap-3 mb-1">
            <div className="flex-1">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Tone preferences</p>
              <p className="text-sm text-gray-600 mt-1">
                Paste a sample of writing in your preferred tone. Wokelo will internalize the voice for every generation.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-[11px] font-semibold text-gray-500" data-testid="tone-toggle-label">
                {toneEnabled ? "On" : "Off"}
              </span>
              <Toggle
                checked={toneEnabled}
                onChange={(v) => saveToggle("tone_enabled", v)}
                testid="tone-toggle"
              />
            </div>
          </div>

          <Textarea
            value={tone}
            onChange={(e) => setTone(e.target.value.slice(0, TONE_MAX))}
            placeholder={"e.g. Sharp, data-led, no fluff. Lead every section with a metric. Avoid hedging language. Write the way a senior partner would brief a CEO."}
            className="min-h-[160px] text-sm mt-3"
            data-testid="tone-textarea"
          />
          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-3">
              <span className="text-[11px] text-gray-400" data-testid="tone-char-count">
                {tone.length} / {TONE_MAX} chars
              </span>
              {toneSavedAt && (
                <span className="text-[11px] text-gray-400" data-testid="tone-saved-at">· Saved {relTime(toneSavedAt)}</span>
              )}
            </div>
            <Button
              onClick={saveTone}
              disabled={savingTone}
              className="bg-black hover:bg-gray-800 text-white"
              data-testid="tone-save-btn"
            >
              {savingTone ? "Saving..." : "Save tone"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ─── Saved prompts library ─────────────────────────────────────
          Users build a reusable library of prompts that show up in the
          searchable dropdown on PPT Studio's "Add Content & Layout"
          and on the Regenerate Slides modal's refinement instructions.
          This table is the only place to edit or delete entries; the
          dropdowns elsewhere only consume the list. */}
      <SavedPromptsManager />
    </div>
  );
}


/**
 * Table of the current user's saved prompts with inline edit + delete.
 *
 * Lives at the bottom of /personalize. Shares its data source
 * (`useSavedPrompts`) with the picker dropdowns on PPT Studio and the
 * Regenerate Slides modal — any add/edit/delete here refreshes the
 * shared cache so siblings pick up the change immediately.
 *
 * Edit UX: clicking the row's pencil swaps that row into edit mode
 * (two text inputs replacing the read-only name + content cells).
 * Confirming PATCHes the backend; cancelling reverts to the original.
 * Delete shows a confirm() so accidental clicks can't silently nuke
 * a prompt the user has carefully crafted.
 */
function SavedPromptsManager() {
  const { prompts, loading, refresh } = useSavedPrompts();
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newContent, setNewContent] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editContent, setEditContent] = useState("");
  const [busy, setBusy] = useState(false);

  const startCreate = () => {
    setCreating(true);
    setNewName("");
    setNewContent("");
  };

  const handleCreate = async () => {
    if (!newName.trim() || !newContent.trim()) {
      toast.error("Both name and content are required.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/me/prompts`, {
        name: newName.trim(),
        content: newContent,
      }, { withCredentials: true });
      toast.success("Prompt saved");
      setCreating(false);
      setNewName("");
      setNewContent("");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save prompt");
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditName(p.name || "");
    setEditContent(p.content || "");
  };

  const handleSaveEdit = async () => {
    if (!editName.trim() || !editContent.trim()) {
      toast.error("Both name and content are required.");
      return;
    }
    setBusy(true);
    try {
      await axios.patch(`${API}/me/prompts/${editingId}`, {
        name: editName.trim(),
        content: editContent,
      }, { withCredentials: true });
      toast.success("Prompt updated");
      setEditingId(null);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update prompt");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (p) => {
    // Native confirm() because Sonner doesn't ship a confirm component
    // and we don't want to drag in another dialog for a one-off destructive
    // action that already shows a clear name in the prompt.
    if (!window.confirm(`Delete "${p.name}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/me/prompts/${p.id}`, { withCredentials: true });
      toast.success(`Deleted "${p.name}"`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't delete prompt");
    }
  };

  return (
    <Card className="border-gray-200 shadow-none" data-testid="saved-prompts-card">
      <CardContent className="pt-5 pb-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
              <BookmarkPlus className="w-3.5 h-3.5" />
              Saved prompts
            </p>
            <p className="text-[11px] text-gray-500 mt-1">
              Build a library of reusable prompts that appear as a quick-pick dropdown on the PPT Studio textareas.
            </p>
          </div>
          {!creating && (
            <Button
              size="sm"
              variant="outline"
              onClick={startCreate}
              data-testid="saved-prompts-add-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              Add prompt
            </Button>
          )}
        </div>

        {/* Inline create form */}
        {creating && (
          <div className="border border-gray-200 rounded-md p-3 space-y-2 bg-gray-50/40" data-testid="saved-prompts-create-form">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Prompt name (e.g., Q4 Market Brief)"
              maxLength={120}
              className="w-full h-9 rounded-md border border-gray-200 px-3 text-sm placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-300"
              data-testid="saved-prompts-new-name"
              autoFocus
            />
            <Textarea
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              placeholder="Prompt content — what should Opus do?"
              maxLength={8000}
              className="min-h-[100px] border-gray-200 text-sm bg-white"
              data-testid="saved-prompts-new-content"
            />
            <div className="flex items-center justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setCreating(false)} disabled={busy} data-testid="saved-prompts-create-cancel">
                Cancel
              </Button>
              <Button size="sm" onClick={handleCreate} disabled={busy} className="bg-black hover:bg-gray-800" data-testid="saved-prompts-create-confirm">
                {busy ? "Saving..." : "Save prompt"}
              </Button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="border border-gray-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2 w-1/3">Name</th>
                <th className="text-left px-3 py-2">Content</th>
                <th className="px-3 py-2 w-24 text-right">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="saved-prompts-table-body">
              {loading ? (
                <tr><td colSpan={3} className="px-3 py-6 text-center text-xs text-gray-400">Loading…</td></tr>
              ) : prompts.length === 0 ? (
                <tr><td colSpan={3} className="px-3 py-6 text-center text-xs text-gray-400" data-testid="saved-prompts-empty">
                  No saved prompts yet. Click &ldquo;Add prompt&rdquo; or save one from the PPT Studio textarea.
                </td></tr>
              ) : prompts.map((p, idx) => {
                const isEdit = editingId === p.id;
                return (
                  <tr key={p.id} className="border-t border-gray-100 align-top" data-testid={`saved-prompts-row-${idx}`}>
                    <td className="px-3 py-2">
                      {isEdit ? (
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          maxLength={120}
                          className="w-full h-8 rounded-md border border-gray-200 px-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-300"
                          data-testid={`saved-prompts-edit-name-${idx}`}
                          autoFocus
                        />
                      ) : (
                        <div className="font-medium text-gray-900 truncate" title={p.name}>{p.name}</div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {isEdit ? (
                        <Textarea
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          maxLength={8000}
                          className="min-h-[80px] border-gray-200 text-sm"
                          data-testid={`saved-prompts-edit-content-${idx}`}
                        />
                      ) : (
                        <div className="text-xs text-gray-600 line-clamp-3 whitespace-pre-wrap">{p.content}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {isEdit ? (
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="outline" onClick={() => setEditingId(null)} disabled={busy} data-testid={`saved-prompts-edit-cancel-${idx}`}>
                            Cancel
                          </Button>
                          <Button size="sm" onClick={handleSaveEdit} disabled={busy} className="bg-black hover:bg-gray-800" data-testid={`saved-prompts-edit-save-${idx}`}>
                            Save
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => startEdit(p)}
                            className="text-gray-400 hover:text-gray-700 p-1 rounded transition-colors"
                            aria-label={`Edit ${p.name}`}
                            data-testid={`saved-prompts-edit-${idx}`}
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleDelete(p)}
                            className="text-gray-400 hover:text-red-500 p-1 rounded transition-colors"
                            aria-label={`Delete ${p.name}`}
                            data-testid={`saved-prompts-delete-${idx}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
